/**
 * JWT Authentication Service for AWS Marketplace integration (auditor console).
 *
 * This service handles:
 * - Token storage in localStorage
 * - Token validation and expiration checking
 * - API calls with automatic Authorization header
 * - Subscription status fetching
 * - Read-only audit queue/detail fetching (GET /audits, GET /audits/{trace_id})
 */

import { API_URL, AUDIT_API_URL } from '../config';

const TOKEN_KEY = 'ainode_jwt_token';
const USER_KEY = 'ainode_user';

/**
 * Parse JWT payload without verification (client-side only).
 * @param {string} token - JWT token
 * @returns {object|null} Decoded payload or null if invalid
 */
function parseJwt(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    // Handle base64url encoding
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
    const decoded = atob(padded);
    return JSON.parse(decoded);
  } catch (e) {
    console.error('Failed to parse JWT:', e);
    return null;
  }
}

/**
 * Check if a JWT token is expired.
 * @param {string} token - JWT token
 * @returns {boolean} True if expired or invalid
 */
function isTokenExpired(token) {
  const payload = parseJwt(token);
  if (!payload || !payload.exp) return true;

  // Add 60 second buffer for clock skew
  const now = Math.floor(Date.now() / 1000);
  return payload.exp < now + 60;
}

/**
 * Get stored JWT token if valid.
 * @returns {string|null} Token or null if not found/expired
 */
export function getToken() {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return null;

  if (isTokenExpired(token)) {
    clearAuth();
    return null;
  }

  return token;
}

/**
 * Get stored user info.
 * @returns {object|null} User object or null
 */
export function getUser() {
  const userJson = localStorage.getItem(USER_KEY);
  if (!userJson) return null;

  try {
    return JSON.parse(userJson);
  } catch {
    return null;
  }
}

/**
 * Store authentication data.
 * @param {string} token - JWT token
 * @param {object} user - User information
 */
export function setAuth(token, user) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

/**
 * Clear authentication data.
 */
export function clearAuth() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

/**
 * Check if user is authenticated.
 * @returns {boolean} True if authenticated with valid token
 */
export function isAuthenticated() {
  return getToken() !== null;
}

/**
 * Get token payload (customer_id, exp, etc.)
 * @returns {object|null} Token payload or null
 */
export function getTokenPayload() {
  const token = getToken();
  if (!token) return null;
  return parseJwt(token);
}

/**
 * Make an authenticated API request.
 * @param {string} endpoint - API endpoint (e.g., '/audits')
 * @param {object} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 */
export async function authFetch(endpoint, options = {}) {
  const token = getToken();

  if (!token) {
    throw new Error('No authentication token available');
  }

  const headers = {
    ...options.headers,
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });

  // Handle 401 Unauthorized - clear auth and throw
  if (response.status === 401) {
    clearAuth();
    throw new Error('Session expired. Please log in again.');
  }

  return response;
}

/**
 * Get subscription status from API.
 * @returns {Promise<object>} Subscription status data
 */
export async function getSubscriptionStatus() {
  const response = await authFetch('/subscription-status');

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to fetch subscription status');
  }

  return response.json();
}

/**
 * Fetch a page of the audit review queue (read-only).
 * @param {object} params - Filters: { status, pack, from, to, limit, nextToken }
 * @returns {Promise<object>} { items, count, next_token? }
 */
export async function fetchAuditQueue(params = {}) {
  const query = new URLSearchParams();
  if (params.status && params.status !== 'all') query.set('status', params.status);
  if (params.pack) query.set('pack', params.pack);
  if (params.from) query.set('from', params.from);
  if (params.to) query.set('to', params.to);
  if (params.limit) query.set('limit', String(params.limit));
  if (params.nextToken) query.set('next_token', params.nextToken);

  const suffix = query.toString() ? `?${query.toString()}` : '';
  const response = await authFetch(`/audits${suffix}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Failed to fetch audit queue (${response.status})`);
  }

  return response.json();
}

/**
 * Fetch the full detail of one audit run (read-only).
 * @param {string} traceId - The run's trace_id
 * @returns {Promise<object>} Run summary + lazy-loaded payload detail
 */
export async function fetchAuditDetail(traceId) {
  const response = await authFetch(`/audits/${encodeURIComponent(traceId)}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Failed to fetch audit detail (${response.status})`);
  }

  return response.json();
}

const AUDIT_TENANT_PACK = 'afip-invoice-audit';

/**
 * Make an authenticated request against the audit executor (evaluate/
 * recommend), which lives on a different base URL than the read-only
 * console API. Mirrors authFetch's 401 handling.
 * @param {string} endpoint - Executor endpoint (e.g. '/evaluate')
 * @param {object} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 */
async function auditExecutorFetch(endpoint, options = {}) {
  const token = getToken();

  if (!token) {
    throw new Error('No authentication token available');
  }

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(`${AUDIT_API_URL}${endpoint}`, {
    ...options,
    headers,
  });

  // Handle 401 Unauthorized - clear auth and throw
  if (response.status === 401) {
    clearAuth();
    throw new Error('Session expired. Please log in again.');
  }

  return response;
}

/**
 * Run a full audit for pasted invoice data: policy evaluation followed by
 * the recommendation step, against the fixed `afip-invoice-audit` pack.
 * @param {object} inputData - Extracted invoice fields (from the extract ainode)
 * @returns {Promise<{trace_id: string}>} The new run's trace_id
 */
export async function runAudit(inputData) {
  const evalResponse = await auditExecutorFetch('/evaluate', {
    method: 'POST',
    body: JSON.stringify({ tenant_pack: AUDIT_TENANT_PACK, input_data: inputData }),
  });

  if (!evalResponse.ok) {
    const error = await evalResponse.json().catch(() => ({}));
    throw new Error(error.error || error.message || `Policy evaluation failed (${evalResponse.status})`);
  }

  const evalResult = await evalResponse.json();
  const { trace_id, results } = evalResult;

  const recommendResponse = await auditExecutorFetch('/recommend', {
    method: 'POST',
    body: JSON.stringify({ trace_id, input_data: inputData, results }),
  });

  if (!recommendResponse.ok) {
    const error = await recommendResponse.json().catch(() => ({}));
    throw new Error(error.error || error.message || `Recommendation failed (${recommendResponse.status})`);
  }

  return { trace_id };
}

/**
 * Extract token from URL after Marketplace redirect.
 * @returns {string|null} Token from URL or null
 */
export function getTokenFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return params.get('token');
}

/**
 * Handle post-registration redirect from Marketplace.
 * Extracts token from URL, validates, stores, and cleans URL.
 * @returns {boolean} True if token was found and stored
 */
export function handleMarketplaceRedirect() {
  const token = getTokenFromUrl();

  if (!token) return false;

  // Validate token format
  if (isTokenExpired(token)) {
    console.error('Received expired token from Marketplace');
    return false;
  }

  // Extract user info from token
  const payload = parseJwt(token);
  const user = {
    customerId: payload.customer_id,
    email: payload.email,
    // Add more fields as needed
  };

  // Store auth data
  setAuth(token, user);

  // Clean URL (remove token parameter)
  const url = new URL(window.location.href);
  url.searchParams.delete('token');
  window.history.replaceState({}, '', url.toString());

  return true;
}
